// The Salesmen tab: results and follow-through per salesman, with a panel for each.
import { useMemo, useState } from "react";
import { getSalesmanScorecard } from "../../distflowAPI/salesAnalyticsApi";
import Answer from "./Answer";
import SalesmanPanel from "./SalesmanPanel";
import { salesmenAnswer } from "./answers";
import SplitView from "./SplitView";
import { plural } from "./customerText";
import { changeText, count, isMaterial, materialTone, money, pct, previousSales } from "./format";
import useSideReport from "./useSideReport";
import "./SalesmanScorecard.css";

export const FLAG_LABELS = {
  low_coverage: "Few accounts ordering",
  losing_customers: "Losing customers",
  shrinking_accounts: "Accounts shrinking",
  no_new_business: "No new business",
};

const COLUMNS = [
  { key: "sales", label: "Sales", value: (row) => Number(row.sales) },
  { key: "coverage_percent", label: "Accounts ordering", value: (row) => Number(row.coverage_percent ?? -1) },
  { key: "retention_percent", label: "Kept customers", comparison: true, value: (row) => Number(row.retention_percent ?? -1) },
  { key: "at_risk_weekly", label: "To call", value: (row) => Number(row.at_risk_weekly) },
];

const SHADES = ["#2e3a66", "#5866a8", "#8f99c9", "#b9c0e0", "#d5daf0"];
const OFFICE_SHADE = "#cdd2dc";
const num = (value) => Number(value || 0);

function Meter({ value, office }) {
  if (value == null) return null;
  return <span className="sa-meter" aria-hidden="true"><b className={office ? "is-office" : ""} style={{ width: `${Math.min(100, Number(value))}%` }} /></span>;
}

/** One line under a salesman's name: the thing worth knowing first. */
function note(row) {
  const flag = (row.flags || [])[0];
  if (flag) return { text: FLAG_LABELS[flag] || flag, tone: "decline" };
  const before = previousSales(row);
  const change = before == null ? 0 : num(row.sales) - before;
  if (row.sales_change_percent != null && change < 0 && isMaterial(change, row.sales_change_percent)) return { text: `Sales down ${money(-change)}`, tone: "decline" };
  if (row.sales_change_percent != null && change > 0 && isMaterial(change, row.sales_change_percent)) return { text: `Sales up ${money(change)}`, tone: "gain" };
  if (num(row.book_accounts) && num(row.book_accounts) < 10) return { text: `Small book, ${count(row.book_accounts)} ${plural(row.book_accounts, "account")}`, tone: "flat" };
  return { text: "Steady", tone: "flat" };
}

/** Two bars: who holds the accounts, and who brings in the sales. */
function Coverage({ rows }) {
  const office = rows.find((row) => row.is_office);
  const reps = rows.filter((row) => !row.is_office);
  const people = [...reps.map((row, index) => ({ row, color: SHADES[index % SHADES.length] })), ...(office ? [{ row: office, color: OFFICE_SHADE }] : [])];
  const accounts = people.reduce((sum, item) => sum + num(item.row.book_accounts), 0);
  const sales = people.reduce((sum, item) => sum + Math.max(0, num(item.row.sales)), 0);
  if (!accounts && !sales) return null;
  const bar = (key, total, label, format) => <div className="sa-cover-row">
    <span className="sa-cover-label">{label} <b>{format(total)}</b></span>
    <div className="sa-cover-bar" role="img" aria-label={`${label}: ${people.map((item) => `${item.row.is_office ? "office" : item.row.name} ${format(num(item.row[key]))}`).join(", ")}`}>
      {people.map((item) => {
        const share = total ? (Math.max(0, num(item.row[key])) / total) * 100 : 0;
        if (share <= 0) return null;
        const text = key === "sales" ? pct(share, 0) : count(item.row[key]);
        return <span key={item.row.key} style={{ width: `${share}%`, background: item.color }} className={item.row.is_office ? "is-office" : ""}>
          {share >= 7 ? (item.row.is_office ? `Office ${text}` : text) : ""}
        </span>;
      })}
    </div>
  </div>;
  return <section className="sa-box sa-cover" aria-labelledby="sa-cover-title">
    <h3 className="sa-box-title" id="sa-cover-title">Who covers the business</h3>
    {bar("book_accounts", accounts, "Accounts", count)}
    {bar("sales", sales, "Sales", money)}
    <div className="sa-cover-legend">{people.map((item) => <span key={item.row.key}><i aria-hidden="true" style={{ background: item.color }} />{item.row.is_office ? "Office (no salesman)" : item.row.name}</span>)}</div>
  </section>;
}

export default function SalesmanScorecard({ request, openKey, onOpen, onClose, onOpenCustomer, onOpenProduct, onFocusSalesman, scope }) {
  const [sort, setSort] = useState({ key: "sales", direction: "desc" });
  const [attempt, setAttempt] = useState(0);
  const params = useMemo(() => ({
    start_date: request?.start_date, end_date: request?.end_date, territory_id: request?.territory_id,
    comparison: request?.comparison, comparison_start_date: request?.comparison_start_date, comparison_end_date: request?.comparison_end_date,
  }), [request]);
  // Team-wide on purpose: the scorecard ignores the salesman and focus filters.
  const scorecard = useSideReport(getSalesmanScorecard, params, Boolean(request?.start_date), attempt);
  const data = scorecard.data;
  const hasComparison = Boolean(data?.comparison);
  const columns = COLUMNS.filter((column) => !column.comparison || hasComparison);

  const all = data?.rows || [];
  const idle = all.filter((row) => !row.is_office && !num(row.sales) && !num(row.active_book_accounts));
  const rows = useMemo(() => {
    const column = COLUMNS.find((item) => item.key === sort.key) || COLUMNS[0];
    return [...(data?.rows || [])].filter((row) => row.is_office || num(row.sales) || num(row.active_book_accounts)).sort((a, b) => {
      if (a.is_office !== b.is_office) return a.is_office ? 1 : -1;
      const difference = column.value(a) - column.value(b);
      return (sort.direction === "asc" ? difference : -difference) || a.name.localeCompare(b.name);
    });
  }, [data, sort]);
  const openRow = all.find((row) => row.key === openKey) || null;
  const answer = salesmenAnswer(data);
  const toggleSort = (key) => setSort((current) => (current.key === key
    ? { key, direction: current.direction === "desc" ? "asc" : "desc" } : { key, direction: "desc" }));
  const panel = openRow ? (mode) => <SalesmanPanel key={openRow.key} mode={mode} row={openRow} team={data.team} comparison={data.comparison}
    request={request} scope={scope} onClose={onClose} onOpenCustomer={onOpenCustomer} onOpenProduct={onOpenProduct} onFocus={onFocusSalesman} /> : null;

  return <>
    <Answer answer={answer} loading={scorecard.loading}>
      {answer?.focus && <button type="button" className="sa-button" onClick={() => onOpen?.(answer.focus.key)}>Open {answer.focus.name}</button>}
    </Answer>
    {data && <Coverage rows={all} />}
    <SplitView panel={panel}>
    <section className="sa-list salesmen" aria-label="Salesmen" aria-busy={scorecard.loading}>
      <div className="sa-list-head">
        <h3>Salesmen</h3>
        <p>Sales follow each order’s salesman; the rest is measured on the accounts assigned to each. Visits and calls aren’t recorded, so this shows results, not effort.</p>
      </div>
      {scorecard.failed && <p className="sa-note" role="alert">The scorecard could not be loaded.
        <button type="button" className="sa-link-button" onClick={() => setAttempt(attempt + 1)}>Try again</button></p>}
      {!data && scorecard.loading && <p className="sa-list-empty">Scoring the team…</p>}
      {data && (rows.length ? <div className="sa-table-scroll"><table className="sa-list-table" aria-label="Salesman scorecard">
        <thead><tr>
          <th scope="col" className="sa-col-name">Salesman</th>
          {columns.map((column) => <th scope="col" key={column.key} className={column.key === "sales" ? "sa-num" : ""}
            aria-sort={sort.key === column.key ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>
            <button type="button" onClick={() => toggleSort(column.key)}>{column.label}<span aria-hidden="true">{sort.key === column.key ? (sort.direction === "asc" ? " ▲" : " ▼") : ""}</span></button>
          </th>)}
        </tr></thead>
        <tbody>{rows.map((row) => {
          const line = row.is_office ? { text: "Accounts with no salesman", tone: "flat" } : note(row);
          return <tr key={row.key} className={`${openKey === row.key ? "is-open" : ""}${row.is_office ? " is-muted" : ""}`} onClick={() => onOpen?.(row.key)}>
            <th scope="row" className="sa-col-name">
              <button type="button" className="sa-row-link" aria-current={openKey === row.key ? "true" : undefined} aria-controls="sa-detail-panel"
                onClick={(event) => { event.stopPropagation(); onOpen?.(row.key); }}>{row.is_office ? "Office" : row.name}</button>
              <small className={`sa-delta--${line.tone}`}>{line.text}</small>
            </th>
            <td className="sa-num" data-label="Sales"><span className="sa-strong">{money(row.sales)}</span>
              {hasComparison
                ? <small className={`sa-delta--${materialTone(num(row.sales) - (previousSales(row) ?? num(row.sales)), row.sales_change_percent)}`}>{row.sales_change_percent == null ? "no sales before" : changeText(num(row.sales) - (previousSales(row) ?? num(row.sales)), row.sales_change_percent)}</small>
                : <small>{count(row.orders)} {plural(row.orders, "order")}</small>}</td>
            <td data-label="Accounts ordering">{row.book_accounts ? <><span className="sa-strong">{count(row.active_book_accounts)} of {count(row.book_accounts)} ({pct(row.coverage_percent, 0)})</span>
              <Meter value={row.coverage_percent} office={row.is_office} /></> : "—"}</td>
            {hasComparison && <td data-label="Kept customers">{row.previous_book_customers
              ? <span className="sa-strong">{count(row.retained_customers)} of {count(row.previous_book_customers)} ({pct(row.retention_percent, 0)})</span> : "—"}</td>}
            <td data-label="To call">{row.at_risk_customers ? <><span className="sa-strong">{count(row.at_risk_customers)} {plural(row.at_risk_customers, "customer")}</span>
              <small>{money(row.at_risk_weekly)} a week</small></> : <span className="sa-muted">None</span>}</td>
          </tr>;
        })}</tbody>
      </table></div> : <p className="sa-list-empty">No salesman activity in these dates.</p>)}
      {idle.length > 0 && <p className="sa-list-foot">{listNames(idle.map((row) => row.name))} {idle.length === 1 ? "has" : "have"} {count(idle.reduce((sum, row) => sum + num(row.book_accounts), 0))} {plural(idle.reduce((sum, row) => sum + num(row.book_accounts), 0), "account")} and no sales in these dates.</p>}
    </section>
  </SplitView>
  </>;
}

function listNames(names) {
  return names.length <= 1 ? names[0] || "" : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}
